import * as React from "react";
import { useRecordContext, useNotify } from "react-admin";
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Tooltip,
    Typography,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import CheckIcon from "@mui/icons-material/Check";
import { adminPatch } from "../../config/api";
import useModerationAction from "../../hooks/useModerationAction";

export default function ValidateSubjectVariantButton() {
    const record = useRecordContext();
    const notify = useNotify();
    const { loading, run } = useModerationAction();
    const [confirmOpen, setConfirmOpen] = React.useState(false);
    const [parentLoading, setParentLoading] = React.useState(false);
    const [parentError, setParentError] = React.useState("");

    if (!record || record.status === "VALIDATED") return null;

    const subjectDbId =
        record?.subjectDbId ||
        record?.subject?.id ||
        null;
    const parentStatusRaw =
        record?.subjectStatus ||
        record?.parentStatus ||
        record?.subject?.status ||
        null;
    const parentStatus = parentStatusRaw
        ? String(parentStatusRaw).toUpperCase()
        : "PENDING";
    const parentValidated = parentStatus === "VALIDATED";
    const subjectLabel =
        record?.subject ||
        record?.subjectLabel ||
        record?.subject?.subject ||
        "ce sujet";

    const closeDialog = () => {
        if (parentLoading) return;
        setConfirmOpen(false);
        setParentError("");
    };

    const validateVariant = async () => {
        await run({
            id: record.id,
            missingIdMessage: "ID variante manquant.",
            request: () => adminPatch(`/subject-variants/${record.id}/validate`),
            successMessage: "Variante validée",
        });
    };

    const onClick = async (event) => {
        event?.stopPropagation?.();
        if (!parentValidated) {
            setConfirmOpen(true);
            return;
        }
        await validateVariant();
    };

    const handleConfirmParentValidation = async () => {
        if (!subjectDbId) {
            notify("ID interne du subject introuvable pour valider le canonique.", { type: "warning" });
            return;
        }
        setParentLoading(true);
        setParentError("");
        try {
            await adminPatch(`/subjects/${subjectDbId}/validate`);
            notify("Sujet canonique validé", { type: "info" });
            await validateVariant();
            setConfirmOpen(false);
            setParentError("");
        } catch (error) {
            setParentError(error?.message || "Impossible de valider le sujet canonique.");
        } finally {
            setParentLoading(false);
        }
    };

    return (
        <>
            <Tooltip
                title={
                    parentValidated
                        ? "Valider cette variante"
                        : "Valider le sujet canonique puis la variante"
                }
            >
        <span>
          <Button
              onClick={onClick}
              disabled={loading}
              variant="contained"
              size="small"
              sx={{
                  bgcolor: "#2e7d32",
                  color: "white",
                  borderRadius: "18px",
                  textTransform: "none",
                  px: 1.25,
                  py: 0.25,
                  "&:hover": { bgcolor: "#1b5e20" },
              }}
              startIcon={
                  loading ? (
                      <CircularProgress size={14} sx={{ color: "white" }} />
                  ) : (
                      <CheckIcon fontSize="small" />
                  )
              }
          >
            Valider
          </Button>
        </span>
            </Tooltip>

            <Dialog
                open={confirmOpen}
                onClose={closeDialog}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Valider le sujet canonique ?</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" sx={{ mb: 1.5 }}>
                        Le sujet <strong>{subjectLabel}</strong> est encore en statut{" "}
                        <strong>{parentStatus}</strong>. Validez le sujet nu pour pouvoir approuver cette variante.
                    </Typography>
                    {parentError && <Alert severity="error">{parentError}</Alert>}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDialog} disabled={parentLoading}>
                        Annuler
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleConfirmParentValidation}
                        disabled={parentLoading}
                        startIcon={
                            parentLoading ? (
                                <CircularProgress size={14} sx={{ color: "white" }} />
                            ) : undefined
                        }
                    >
                        Valider le sujet
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
